import { zodReportIssue } from './issueReporter';
import z from 'zod';

/**
 * Defines a fallback value to use if an invalid value is encountered
 * @param schema `z.literal()` schema
 * @param defaultValue Value to return if invalid value is passed
 */
export function zodFallback<const T extends z.ZodLiteral, V extends string>(
    schema: T,
    defaultValue: V,
): T extends z.ZodLiteral<infer Values> ? z.ZodLiteral<Values | V> : never;
/**
 * Defines a fallback value to use if an invalid value is encountered
 * @param schema `z.enum()` schema
 * @param defaultValue Value to return if invalid value is passed
 */
export function zodFallback<const T extends z.ZodEnum, V extends string>(
    schema: T,
    defaultValue: V,
): T extends z.ZodEnum<infer Values> ? z.ZodEnum<z.core.util.ToEnum<Values[keyof Values] | V>> : never;
/**
 * Defines a fallback value to use if an invalid value is encountered
 * @param schema `z.discriminatedUnion()` schema
 * @param defaultSchema Schema that the `defaultValue` fulfills
 * @param defaultValue Value to return if invalid value is passed
 */
export function zodFallback<const T extends z.ZodDiscriminatedUnion, S extends z.core.$ZodTypeDiscriminable, V extends z.core.output<S>>(
    schema: T,
    defaultSchema: S,
    defaultValue: V,
): T extends z.ZodDiscriminatedUnion<infer Options> ? z.ZodDiscriminatedUnion<[...Options, S]> : never;
/**
 * Defines a fallback value to use if an invalid value is encountered
 * @param schema Zod schema
 * @param defaultValue Value to return if invalid value is passed
 */
export function zodFallback<const T extends z.ZodType, S extends string | z.core.SomeType, V extends z.core.output<S>>(
    schema: T,
    defaultSchema: S,
    defaultValue?: V,
) {
    if (schema instanceof z.ZodLiteral) {
        if (typeof defaultSchema !== 'string') {
            throw new Error(`defaultValue must be of type string, got: ${typeof defaultSchema}`);
        }

        const literalSchema = z.literal([...schema.values, defaultSchema]);
        return literalSchema.catch((ctx) => {
            zodReportIssue('fallback', ctx);
            return defaultSchema;
        });
    }

    if (schema instanceof z.ZodEnum) {
        if (typeof defaultSchema !== 'string') {
            throw new Error(`defaultValue must be of type string, got: ${typeof defaultSchema}`);
        }

        const enumSchema = z.enum([...Object.keys(schema.enum), defaultSchema]);
        return enumSchema.catch((ctx) => {
            zodReportIssue('fallback', ctx);
            return defaultSchema;
        });
    }

    if (schema instanceof z.ZodDiscriminatedUnion) {
        if (!defaultSchema) {
            throw new Error('defaultSchema must be zod Schema, not undefined');
        }

        const discriminatedUnionSchema = z.discriminatedUnion(schema.def.discriminator, [
            ...(schema.options as unknown as [z.core.$ZodTypeDiscriminable, ...z.core.$ZodTypeDiscriminable[]]),
            defaultSchema as unknown as z.core.$ZodTypeDiscriminable,
        ]);
        return discriminatedUnionSchema.catch((ctx: z.core.$ZodCatchCtx) => {
            zodReportIssue('fallback', ctx);
            return defaultValue;
        });
    }

    if (typeof defaultSchema === 'string') {
        return schema;
    }

    const caughtSchema = schema.or(defaultSchema);
    return caughtSchema.catch((ctx: z.core.$ZodCatchCtx) => {
        zodReportIssue('fallback', ctx);
        return defaultValue;
    });
}
